import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import server from '../global/server';
import { getServerNow } from '../hooks/useServerTime';

/**
 * Dialog de conflito de horario.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - conflicts: { hasConflict, asHost, asConviva } (resposta de /booking/conflicts)
 * - onResolved: () => void — chamado apos resolver o conflito (cancelar encontro anterior)
 */
const ConflictDialog = ({ open, onClose, conflicts, onResolved }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!conflicts?.hasConflict) return null;

    const hasHostConflict = conflicts.asHost?.length > 0;
    const convivaConflict = conflicts.asConviva?.[0]; // Pega o primeiro conflito como conviva

    const now = getServerNow();
    let hoursUntil = null;
    let isLessThan24h = false;
    let isFree = false;
    let amount = 0;

    if (convivaConflict) {
        const meetingTime = new Date(convivaConflict.datetime);
        hoursUntil = (meetingTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        isLessThan24h = hoursUntil < 24;
        isFree = convivaConflict.free;
        amount = convivaConflict.amount || 0;
    }

    const formatDateTime = (dt) => {
        const d = new Date(dt);
        return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    };

    const handleCancel = async () => {
        if (!convivaConflict) return;
        setLoading(true);
        setError(null);
        try {
            await server.delete('/booking/cancel', {
                data: {
                    order_id: convivaConflict.order_id,
                    accept_no_refund: isLessThan24h,
                },
            });
            onResolved?.();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Erro ao cancelar encontro');
        } finally {
            setLoading(false);
        }
    };

    // Caso 1: Conflito como anfitriao — bloqueio total
    if (hasHostConflict) {
        const conflict = conflicts.asHost[0];
        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle fontWeight={600}>Conflito de horário</DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>
                        Você já possui o encontro <strong>"{conflict.title}"</strong> criado para{' '}
                        <strong>{formatDateTime(conflict.datetime)}</strong>.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Não e possível criar outro encontro neste horário. Os encontros devem ter no mínimo 1 hora de intervalo.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="contained" fullWidth sx={{ bgcolor: '#006a61', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#005a52' } }}>
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Caso 2: Conflito como conviva
    if (convivaConflict) {
        // Caso 2a: Gratuito + menos de 24h — bloqueio
        if (isFree && isLessThan24h) {
            return (
                <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                    <DialogTitle fontWeight={600}>Conflito de horário</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" sx={{ borderRadius: '12px', mb: 2 }}>
                            Você tem o encontro gratuito <strong>"{convivaConflict.title}"</strong> agendado para{' '}
                            <strong>{formatDateTime(convivaConflict.datetime)}</strong>.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Cancelamentos de encontros gratuitos com menos de 24h de antecedência não são permitidos.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={onClose} variant="contained" fullWidth sx={{ bgcolor: '#006a61', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#005a52' } }}>
                            Entendi
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        }

        // Caso 2b: Pago — mostrar info de reembolso
        // Caso 2c: Gratuito + mais de 24h — cancelar direto
        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle fontWeight={600}>Conflito de horário</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                            Você já tem o encontro <strong>"{convivaConflict.title}"</strong> agendado para{' '}
                            <strong>{formatDateTime(convivaConflict.datetime)}</strong>.
                        </Alert>

                        <Typography variant="body2" color="text.secondary">
                            Para prosseguir, e necessário cancelar o encontro anterior.
                        </Typography>

                        {!isFree && (
                            <Box sx={{ bgcolor: '#f9f9f9', borderRadius: '8px', p: 2 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                    Política de cancelamento:
                                </Typography>
                                {isLessThan24h ? (
                                    <Alert severity="error" variant="outlined" sx={{ borderRadius: '8px' }}>
                                        <Typography variant="body2">
                                            Faltam menos de 24h para o encontro. O cancelamento será feito <strong>sem reembolso</strong> do valor de R$ {Number(amount).toFixed(2).replace('.', ',')}.
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <Alert severity="success" variant="outlined" sx={{ borderRadius: '8px' }}>
                                        <Typography variant="body2">
                                            Faltam mais de 24h para o encontro. Você receberá o <strong>reembolso integral</strong> de R$ {Number(amount).toFixed(2).replace('.', ',')}.
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
                    <LoadingButton
                        onClick={handleCancel}
                        loading={loading}
                        variant="contained"
                        fullWidth
                        color="error"
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                    >
                        {!isFree && isLessThan24h
                            ? 'Cancelar encontro anterior (sem reembolso)'
                            : 'Cancelar encontro anterior e prosseguir'
                        }
                    </LoadingButton>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        fullWidth
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500, borderColor: '#ccc', color: '#666' }}
                    >
                        Manter encontro atual
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return null;
};

export default ConflictDialog;
